import React, { useMemo, useState } from "react";
import {
  MyMessage,
  InputField,
  TextArea,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import { useDoSeeDataContext } from "../../../context/DoSeeDataContext";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import Pricing from "../../components/Pricing";
import GenerateDescription from "../utils/GenerateDescription";
import {
  ContactSection,
  SocialSection,
  GeoSection,
  VisibilitySection,
  DescriptionSection,
} from "../utils/AddSections";
import {
  FiImage,
  FiClock,
  FiDollarSign,
  FiUsers,
  FiRepeat,
} from "react-icons/fi";
import { FaGlassCheers } from "react-icons/fa";

const toSlug = (s: any) =>
  (s || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_");

const toNumber = (v: any, fallback: any = 0) =>
  Number.isNaN(parseFloat(v)) ? fallback : parseFloat(v);

const REPEAT_OPTIONS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
  "annual",
];

const Add = () => {
  const { createOneContent } = useDoSeeDataContext();

  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
    tags,
  } = useGeoPositionContext();

  const [thumbnail, setThumbnail] = useState(defaultImage);
  const [thumbnailId, setThumbnailId] = useState("img_cdn_id");

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [title, setTitle] = useState("");
  const [venue, setVenue] = useState("");
  const [venueCapacity, setVenueCapacity] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const [fee, setFee] = useState<any>(0);
  const [vip, setVip] = useState<any>(0);
  const [vvip, setVvip] = useState<any>(0);

  const [email, setEmail] = useState("");
  const [payLink, setPayLink] = useState<any>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [whatsapp, setWhatsapp] = useState("none");
  const [facebook, setFacebook] = useState("none");
  const [twitter, setTwitter] = useState("none");
  const [instagram, setInstagram] = useState("none");
  const [youtube, setYoutube] = useState("none");
  const [hashtags, setHashtags] = useState("");

  const [googleMapCode, setGoogleMapCode] = useState("none");
  const [latitude, setLatitude] = useState("none");
  const [longitude, setLongitude] = useState("none");
  const [formattedAddress, setFormattedAddress] = useState("");

  const [featured, setFeatured] = useState<any>("no");
  const [promoted, setPromoted] = useState<any>("no");
  const [partyStatus, setPartyStatus] = useState(1);
  const [type, setType] = useState<"public" | "private">("public");

  const [guests, setGuests] = useState("");

  const [expo, setExpo] = useState(0);
  const [showTop, setShowTop] = useState(0);
  const [pricing, setPricing] = useState<any[]>([]);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);

  const [repeatEveryList, setRepeatEveryList] = useState<string[]>([]);
  const [description, setDescription] = useState("");

  const partyRoutes = "/data/party";

  const toggleRepeat = (val: string) => {
    setRepeatEveryList((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const repeatEveryString = useMemo(
    () => (repeatEveryList.length ? repeatEveryList.join("|") : null),
    [repeatEveryList]
  );

  const responses = (isError: any, msg: any) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(msg || "");
  };

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);
    setThumbnail(defaultImage);
    setThumbnailId("img_cdn_id");

    setTitle("");
    setVenue("");
    setVenueCapacity("");
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");

    setFee(0);
    setVip(0);
    setVvip(0);

    setEmail("");
    setPayLink(null);
    setPhoneNumber("");
    setWhatsapp("none");
    setFacebook("none");
    setTwitter("none");
    setInstagram("none");
    setYoutube("none");
    setHashtags("");

    setGoogleMapCode("none");
    setLatitude("none");
    setLongitude("none");
    setFormattedAddress("");

    setFeatured("no");
    setPromoted("no");
    setPartyStatus(1);
    setType("public");
    setGuests("");

    setExpo(0);
    setShowTop(0);
    setPricing([]);
    setRepeatEveryList([]);
    setDescription("");
  };

  const validate = () => {
    if (!title?.trim()) return "Title is required.";
    if (!thumbnail || !thumbnailId) return "Image is required.";
    if (type === "private" && !guests?.trim())
      return "Add at least one guest for a private party.";
    if (startDate && endDate && new Date(startDate) > new Date(endDate))
      return "End date cannot be before start date.";
    return null;
  };

  const addOnePartyHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    const err = validate();
    if (err) {
      responses(true, err);
      return;
    }
    await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const data = {
      country_id,
      first_level_id: first_level_id || 0,
      second_level_id: second_level_id || 0,
      third_level_id: third_level_id || 0,
      fourth_level_id: fourth_level_id || 0,

      title: title?.trim(),
      slug: title ? toSlug(title) : null,
      description: description || null,

      venue: venue || null,
      venue_capacity: venueCapacity ? toNumber(venueCapacity, null) : null,

      start_date: startDate || null,
      end_date: endDate || null,
      start_time: startTime || null,
      end_time: endTime || null,

      fee: toNumber(fee, 0),
      vip: toNumber(vip, 0),
      vvip: toNumber(vvip, 0),

      thumbnail: thumbnail || defaultImage,
      image_cdn_id: thumbnailId || "",

      phone_number: phoneNumber || null,
      email_address: email || null,
      py_link: payLink,
      whatsapp: whatsapp || "none",
      facebook: facebook || "none",
      twitter: twitter || "none",
      instagram: instagram || "none",
      youtube: youtube || "none",

      hashtag: `${tags || ""}${hashtags ? `, ${hashtags}` : ""}`.trim(),

      audio_adv: "none",
      google_map_code: googleMapCode || "none",
      latitude: latitude || "none",
      longitude: longitude || "none",
      formatted_address: formattedAddress || null,
      country_short: "none",

      status: partyStatus,
      featured,
      promoted,
      repeat_every: repeatEveryString,
      type,
      guests: guests
        ? guests
            .split(",")
            .map((g) => g.trim())
            .filter(Boolean)
        : [],
      expo,
      show_top: showTop,
      pricing,
    };

    const { status, message } = await createOneContent({
      data,
      content: _TYPES_.PARTY,
    });

    if (status === 200 || status === 201) {
      cleanFields();
      responses(false, message);
    } else {
      responses(true, message);
    }
  };

  const clearMessages = () => {
    setError(null as any);
    setSuccess(null as any);
  };

  const chipsBase =
    "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs cursor-pointer select-none transition-colors";
  const chipOn = "border-brand bg-brand/10 text-brand";
  const chipOff =
    "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50";

  return (
    <AddPageShell
      backRoute={partyRoutes}
      eyebrow="DoSee Data • Parties"
      title="Add Party"
      subtitle={`List a party experience in ${entry_label || "this destination"}.`}
      icon={<FaGlassCheers className="h-5 w-5" />}
      pills={type === "private" ? [{ label: "Private" }] : undefined}
      modals={
        showDescriptionModal ? (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null
      }
      loading={loading}
      submitLabel="Add Party"
      onSubmit={addOnePartyHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage("");
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Cover Image"
        hint="The party flyer or hero image."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={image_kit_folder(country_name, FOLDERS.PARTY)}
        />
      </AddSection>

      <AddSection title="Party Details" hint="Title, venue and party type.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "title",
              title: "Title",
              type: "text",
              required: true,
              value: title,
              onChange: (e: any) => {
                setTitle(e.target.value);
                clearMessages();
              },
            }}
          />
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold text-gray-600">Type</p>
            <div className="inline-flex w-fit rounded-lg border border-gray-200 bg-white p-1">
              <button
                type="button"
                onClick={() => setType("public")}
                className={`rounded-md px-3 py-1.5 text-sm transition ${
                  type === "public"
                    ? "bg-gradient-to-br from-brand to-primary text-white"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                Public
              </button>
              <button
                type="button"
                onClick={() => setType("private")}
                className={`rounded-md px-3 py-1.5 text-sm transition ${
                  type === "private"
                    ? "bg-gradient-to-br from-brand to-primary text-white"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                Private
              </button>
            </div>
          </div>
          <InputField
            props={{
              id: "venue",
              title: "Venue",
              type: "text",
              value: venue,
              onChange: (e: any) => {
                setVenue(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "venue_capacity",
              title: "Venue Capacity",
              type: "number",
              value: venueCapacity,
              onChange: (e: any) => {
                setVenueCapacity(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "formatted_address",
              title: "Formatted Address",
              type: "address",
              value: formattedAddress,
              onChange: (e: any) => {
                setFormattedAddress(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Date & Time"
        hint="When the party happens."
        icon={<FiClock className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <InputField
            props={{
              id: "start_date",
              title: "Start Date",
              type: "date",
              value: startDate,
              onChange: (e: any) => setStartDate(e.target.value),
            }}
          />
          <InputField
            props={{
              id: "end_date",
              title: "End Date",
              type: "date",
              value: endDate,
              onChange: (e: any) => setEndDate(e.target.value),
            }}
          />
          <InputField
            props={{
              id: "start_time",
              title: "Start Time",
              type: "time",
              value: startTime,
              onChange: (e: any) => setStartTime(e.target.value),
            }}
          />
          <InputField
            props={{
              id: "end_time",
              title: "End Time",
              type: "time",
              value: endTime,
              onChange: (e: any) => setEndTime(e.target.value),
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Repeat Schedule"
        hint="Optional — recurring days or annual."
        icon={<FiRepeat className="h-4 w-4" />}
      >
        <div className="flex flex-wrap gap-2">
          {REPEAT_OPTIONS.map((opt) => {
            const on = repeatEveryList.includes(opt);
            return (
              <span
                key={opt}
                role="button"
                tabIndex={0}
                onClick={() => toggleRepeat(opt)}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && toggleRepeat(opt)
                }
                className={`${chipsBase} ${on ? chipOn : chipOff} capitalize`}
              >
                {on ? (
                  <MdCheckBox className="text-base" />
                ) : (
                  <MdCheckBoxOutlineBlank className="text-base" />
                )}
                {opt}
              </span>
            );
          })}
        </div>
      </AddSection>

      <AddSection
        title="Pricing"
        hint="Optional pricing tiers."
        icon={<FiDollarSign className="h-4 w-4" />}
      >
        <Pricing pricing={pricing} setPricing={setPricing} />
      </AddSection>

      <ContactSection
        phone={phoneNumber}
        setPhone={setPhoneNumber}
        email={email}
        setEmail={setEmail}
        payLink={payLink}
        setPayLink={setPayLink}
        clearMessages={clearMessages}
      />

      <SocialSection
        whatsapp={whatsapp}
        setWhatsapp={setWhatsapp}
        facebook={facebook}
        setFacebook={setFacebook}
        twitter={twitter}
        setTwitter={setTwitter}
        instagram={instagram}
        setInstagram={setInstagram}
        youtube={youtube}
        setYoutube={setYoutube}
        clearMessages={clearMessages}
      />

      <GeoSection
        googleMapCode={googleMapCode}
        setGoogleMapCode={setGoogleMapCode}
        latitude={latitude}
        setLatitude={setLatitude}
        longitude={longitude}
        setLongitude={setLongitude}
        clearMessages={clearMessages}
      />

      <VisibilitySection
        statusId="party_status"
        setStatus={setPartyStatus}
        setFeatured={setFeatured}
        setPromoted={setPromoted}
        setExpo={setExpo}
        clearMessages={clearMessages}
      />

      {type === "private" ? (
        <AddSection
          title="Private Guest List"
          hint="Comma-separated email addresses."
          icon={<FiUsers className="h-4 w-4" />}
        >
          <TextArea
            props={{
              id: "private_party_guests",
              title: "Guests List",
              type: "text",
              value: guests,
              onChange: (e: any) => setGuests(e.target.value),
            }}
          />
        </AddSection>
      ) : null}

      <DescriptionSection
        description={description}
        setDescription={setDescription}
        hashtags={hashtags}
        setHashtags={setHashtags}
        onGenerate={() => setShowDescriptionModal(true)}
        clearMessages={clearMessages}
      />
    </AddPageShell>
  );
};

export default Add;
